import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import intranetApi from '../../services/intranetApi';
import api from '../../utils/api';
import RichTextEditor from '../common/RichTextEditor';
import { renderContentWithMentions } from '../../utils/mentions';

const CreatePost = ({ isOpen, onClose, onPostCreated }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    post_type: 'general',
    category_id: '',
    visibility: 'company',
    department_id: '',
    is_required_reading: false
  });

  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewMode, setPreviewMode] = useState(false);

  // Fetch initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  const fetchInitialData = async () => {
    try {
      const [categoriesResponse, departmentsResponse, userResponse] = await Promise.all([
        intranetApi.config.getPostCategories(),
        api.get('/departments'),
        api.get('/auth/me')
      ]);

      setCategories(categoriesResponse.data.categories || []);
      setDepartments(departmentsResponse.data.departments || []);
      setCurrentUser(userResponse.data.user);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load form data');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-select department for department posts
    if (name === 'visibility' && value === 'department' && currentUser?.department_id) {
      setFormData(prev => ({
        ...prev,
        department_id: currentUser.department_id.toString()
      }));
    }

    // Clear department when switching to company-wide
    if (name === 'visibility' && value === 'company') {
      setFormData(prev => ({
        ...prev,
        department_id: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length > 10000) {
      newErrors.content = 'Content must be less than 10,000 characters';
    }

    if (formData.visibility === 'department' && !formData.department_id) {
      newErrors.department_id = 'Department is required for department posts';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const postData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        post_type: formData.post_type,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        visibility: formData.visibility,
        department_id: formData.department_id ? parseInt(formData.department_id) : null,
        is_required_reading: formData.is_required_reading
      };

      const response = await intranetApi.posts.createPost(postData);
      
      if (response.data.success) {
        toast.success('Post created successfully!');
        onPostCreated(response.data.post);
        handleClose();
      } else {
        throw new Error(response.data.error || 'Failed to create post');
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.response?.data?.error || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      post_type: 'general',
      category_id: '',
      visibility: 'company',
      department_id: '',
      is_required_reading: false
    });
    setErrors({});
    setPreviewMode(false);
    onClose();
  };

  const formatPreviewContent = (content) => {
    return renderContentWithMentions(content, 'text-gray-700 text-sm leading-relaxed');
  };

  const getSelectedCategoryColor = () => {
    if (!formData.category_id) return '#6B7280';
    const category = categories.find(cat => cat.id.toString() === formData.category_id);
    return category?.color || '#6B7280';
  };

  const getSelectedCategoryLabel = () => {
    if (!formData.category_id) return null;
    const category = categories.find(cat => cat.id.toString() === formData.category_id);
    return category?.display_label;
  };

  const getSelectedDepartmentName = () => {
    if (!formData.department_id) return null;
    const department = departments.find(dept => dept.id.toString() === formData.department_id);
    return department?.name;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Create New Post</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                previewMode 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <EyeIcon className="h-4 w-4 mr-1.5 inline" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content - Made scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {previewMode ? (
            /* Preview Mode */
            <div className="p-6">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Preview Header */}
                <div className="p-6 pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                          {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{currentUser?.name || 'Your Name'}</p>
                        <p className="text-xs text-gray-500">Just now</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {formData.is_required_reading && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Required Reading
                        </span>
                      )}
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {formData.visibility === 'company' ? (
                          <>
                            <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                            Company-wide
                          </>
                        ) : (
                          <>
                            <UserGroupIcon className="h-3 w-3 mr-1" />
                            {getSelectedDepartmentName() || 'Department'}
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Category Preview */}
                  {getSelectedCategoryLabel() && (
                    <div className="mt-3">
                      <span 
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: getSelectedCategoryColor() }}
                      >
                        {getSelectedCategoryLabel()}
                      </span>
                    </div>
                  )}

                  {/* Content Preview */}
                  <div className="mt-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {formData.title || 'Post Title'}
                    </h4>
                    <div className="text-gray-700 text-sm leading-relaxed">
                      {formData.content ? formatPreviewContent(formData.content) : (
                        <p className="text-gray-400 italic">Post content will appear here...</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Preview Actions */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center text-gray-500 text-sm">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        0
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Post Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter a compelling title for your post..."
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                  maxLength={200}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.title.length}/200 characters
                </p>
              </div>

              {/* Content */}
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  Post Content *
                </label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                  placeholder="Write your post content here... You can use rich formatting and mention users with @username"
                  className="mb-2"
                  maxLength={10000}
                  error={errors.content}
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                )}
              </div>

              {/* Form Row 1: Category and Post Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Category */}
                <div>
                  <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    id="category_id"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select a category (optional)</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.display_label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Post Type */}
                <div>
                  <label htmlFor="post_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Post Type
                  </label>
                  <select
                    id="post_type"
                    name="post_type"
                    value={formData.post_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="general">General</option>
                    <option value="announcement">Announcement</option>
                    <option value="news">News</option>
                    <option value="update">Update</option>
                  </select>
                </div>
              </div>

              {/* Form Row 2: Visibility and Department */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Visibility */}
                <div>
                  <label htmlFor="visibility" className="block text-sm font-medium text-gray-700 mb-2">
                    Visibility *
                  </label>
                  <select
                    id="visibility"
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="company">Company-wide</option>
                    <option value="department">Department Only</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.visibility === 'company' 
                      ? 'Visible to all company members'
                      : 'Visible only to department members'
                    }
                  </p>
                </div>

                {/* Department (conditional) */}
                {formData.visibility === 'department' && (
                  <div>
                    <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-2">
                      Department *
                    </label>
                    <select
                      id="department_id"
                      name="department_id"
                      value={formData.department_id}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                        errors.department_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a department</option>
                      {departments.map(department => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </select>
                    {errors.department_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.department_id}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Required Reading */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="is_required_reading"
                    name="is_required_reading"
                    type="checkbox"
                    checked={formData.is_required_reading}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="is_required_reading" className="text-sm font-medium text-gray-700">
                    Mark as Required Reading
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    <ExclamationTriangleIcon className="h-3 w-3 inline mr-1" />
                    Users will need to acknowledge they have read this post
                  </p>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-500">
            {previewMode ? (
              'Preview mode - switch back to edit to make changes'
            ) : (
              'Fill out the form above to create your post'
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || previewMode}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Create Post
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePost; 